import { CreativeLayer, Rectangle } from 'content-creation-common/models';
import { createSelector } from 'reselect';
import { getAllContentById } from '../../../../selectors/content';
import {
  getAllElements,
  getAllElementsById,
} from '../../../../selectors/element';
import { getAllMediaById } from '../../../../selectors/media';
import { getSelectedDisplayElementId } from '../../shared/state-management/selectors';
import { inSimpleFlow as getIsInSimpleFlow } from '../../../../features/routes/state-management/selectors';
import {
  getCreativeLayerByElementId,
} from '../../../creative/state-management/selectors';
import { getIsPublisherHappeningNow } from '../../../discover/state-management/selectors';
import {
  isEmpty as isEmptyObject,
  isNil,
  isNotNil,
} from '../../../../util/object';
import Layer from '../../../../model/Layer';
import { SIMPLE_LAYER_TYPE_SET } from '../constants';
import { TEMPLATE_LAYER_TYPES_WITH_BOUNDS } from '../../../../constants';

export const getIsInitialized = (state) => {
  return state.simpleFlow.isInitialized;
};

export const getSourceTemplate = (state) => {
  return state.simpleFlow.sourceTemplate;
};

export const getAllBoundingBoxes = (state) => {
  return state.simpleFlow.boundingBoxByElementId;
};

export const isWarningAboutAdvancedFlow = (state) => {
  return state.simpleFlow.warnUserAdvancedFlow;
};

export const getIsTextPropertiesDisabled = createSelector(
  [getIsInSimpleFlow, getIsPublisherHappeningNow],
  (isInSimpleFlow, isPublisherHappeningNow) => {
    // Happening Now should always get limited editing mode
    return isInSimpleFlow && isPublisherHappeningNow;
  },
);

export const getAllLayers = createSelector(
  [
    getAllElements,
    getAllContentById,
    getAllMediaById,
    getSourceTemplate,
    getSelectedDisplayElementId,
  ],
  (elements, contentById, mediaById, template, selectedElementId) => {
    const validElements = elements.filter(({ contentId }) => {
      // It is currently presumed all elements will have a content id.
      if (isNil(contentId)) return false;

      // Furthermore, each content id should correspond to a content object.
      // If not, we don't want to mess with the element.
      const content = contentById[contentId];
      return isNotNil(content);
    });

    return validElements
      .map((element) => {
        const { contentId } = element;
        const content = contentById[contentId];
        const { mediaId } = content;
        const media = mediaById[mediaId];
        const selected = element.elementId === selectedElementId;
        return Layer.getLayerFromReduxStateObjects(
          element,
          content,
          media,
          template,
          selected,
        );
      })
      .filter((layer) => isNotNil(layer));
  },
);

export const getEditableLayers = createSelector([getAllLayers], (layers) => {
  return layers.filter((layer) => {
    return (
      SIMPLE_LAYER_TYPE_SET.has(layer.contentType) &&
      !layer.excludedFromTemplate &&
      !Layer.isMaskLayer(layer)
    );
  });
});

export const getEditableLayersReversed = createSelector(
  [getEditableLayers],
  (layers) => [...layers].reverse(),
);

export const isEditingAnyTransforms = createSelector(
  [getEditableLayers],
  (layers) => layers.some((layer) => layer.isEditingTransform),
);

const getLayerByElementIdHelper = (
  elementId,
  allElements,
  allContent,
  allMedia,
  sourceTemplate,
  selectedElementId,
) => {
  if (isEmptyObject(allElements)) return null;
  if (isEmptyObject(allContent)) return null;
  if (isEmptyObject(allMedia)) return null;
  if (isNil(elementId)) return null;

  const element = allElements[elementId];
  if (isEmptyObject(element)) return null;

  const content = allContent[element.contentId];
  if (isEmptyObject(content)) return null;

  const media = allMedia[content.mediaId];
  if (isEmptyObject(media)) return null;

  return Layer.getLayerFromReduxStateObjects(
    element,
    content,
    media,
    sourceTemplate,
    elementId === selectedElementId,
  );
};

/**
 * @function getLayerByElementId
 * @param {object} state
 * @param {Id} elementId
 * @return {Layer}
 */
export const getLayerByElementId = createSelector(
  [
    (state, elementId) => elementId,
    getAllElementsById,
    getAllContentById,
    getAllMediaById,
    getSourceTemplate,
    getSelectedDisplayElementId,
  ],
  getLayerByElementIdHelper,
);

export const getSelectedLayer = createSelector(
  [
    getSelectedDisplayElementId,
    getAllElementsById,
    getAllContentById,
    getAllMediaById,
    getSourceTemplate,
  ],
  (selectedElementId, allElements, allContent, allMedia, sourceTemplate) => {
    return getLayerByElementIdHelper(
      selectedElementId,
      allElements,
      allContent,
      allMedia,
      sourceTemplate,
      selectedElementId,
    );
  },
);

export const isLayerEditorOpen = createSelector(
  [getSelectedLayer, (state) => state.simpleFlow.layerEditorIsOpen],
  (selectedLayer, layerEditorIsOpen) => {
    return isNotNil(selectedLayer) && layerEditorIsOpen;
  },
);

export const getLayerEditorMode = (state) => {
  return state.simpleFlow.layerEditorMode;
};

export const getBoundingBoxByElementId = createSelector(
  [getAllBoundingBoxes, (state, elementId) => elementId],
  (bounds, elementId) => {
    if (isNil(bounds) || isNil(elementId) || isNil(bounds[elementId])) {
      return undefined;
    }
    return bounds[elementId];
  },
);

export const getBoundedLayers = createSelector([getEditableLayers], (layers) =>
  layers.filter((layer) =>
    TEMPLATE_LAYER_TYPES_WITH_BOUNDS.has(layer.contentType),
  ),
);

export const getLayerRectangleAtFirstFrameByElementId = createSelector(
  [getCreativeLayerByElementId],
  (creativeLayer) => {
    if (isNil(creativeLayer)) return null;

    const { height, width } = creativeLayer;

    // Just check for 0th frame
    const transform = creativeLayer.getAbsoluteTransformsAtFrame(0);
    if (isNil(transform)) return null;

    const { rotation, scale, translation } = transform;
    return new Rectangle(
      translation.x,
      translation.y,
      width * scale.x,
      height * scale.y,
      rotation,
    );
  },
);

export const getTemplateLayerRectangleAtFirstFrameByElementId = createSelector(
  [getLayerByElementId],
  (layer) => {
    if (!Layer.isLayer(layer) || !Layer.isLayer(layer.originalLayer)) {
      return null;
    }
    const { originalLayer } = layer;

    const creativeLayer = Layer.getCreativeLayer(originalLayer);
    if (!CreativeLayer.isCreativeLayer(creativeLayer)) return null;

    // Just check for 0th frame
    const transform = creativeLayer.getAbsoluteTransformsAtFrame(0);
    if (isNil(transform)) return null;

    const { height, width } = creativeLayer;
    const { rotation, scale, translation } = transform;

    return new Rectangle(
      translation.x,
      translation.y,
      width * scale.x,
      height * scale.y,
      rotation,
    );
  },
);
